// MFAEnrollmentFlow.js
// Shared MFA enrollment UI used by both EnrollMFAPage (pre-session gate)
// and AccountPage (authenticated re-enrollment).
// API calls are injected as props — this component contains only UI and phase state.

import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Button from './Button';
import appStoreBadge from '../assets/app-store-badge.svg';
import googlePlayBadge from '../assets/google-play-badge.svg';

export default function MFAEnrollmentFlow({ onBegin, onConfirm, onSuccess, onCancel }) {
  const [phase, setPhase] = useState('idle');
  const [qrUri, setQrUri] = useState('');
  const [secret, setSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [totpCode, setTotpCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [codesSaved, setCodesSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleBegin = async () => {
    setError(''); setLoading(true);
    try {
      const data = await onBegin();
      setQrUri(data.qr_uri);
      setSecret(data.secret);
      setPhase('enrolling');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to begin enrollment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (totpCode.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app.');
      return;
    }
    setError(''); setLoading(true);
    try {
      const data = await onConfirm(secret, totpCode, trustDevice);
      setRecoveryCodes(data.recovery_codes);
      setPhase('codes');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    onSuccess();
  };

  const handleCancel = () => {
    setPhase('idle');
    setTotpCode('');
    setQrUri('');
    setSecret('');
    setError('');
    setTrustDevice(false);
    setCodesSaved(false);
    if (onCancel) onCancel();
  };

  return (
    <div>
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Idle — show begin button */}
      {phase === 'idle' && (
        <div style={styles.actions}>
          <Button onClick={handleBegin} disabled={loading}>
            {loading ? 'Loading...' : 'Set Up Authenticator App'}
          </Button>
        </div>
      )}

      {/* Step 1: QR code + TOTP entry */}
      {phase === 'enrolling' && (
        <div style={styles.enrollmentCard}>
          <div style={styles.enrollmentStep}>
            <div style={styles.stepNum}>1</div>
            <div style={styles.stepText}>
              <strong>Scan this QR code</strong> with your authenticator app
              (Google Authenticator, Microsoft Authenticator, or Authy).
            </div>
          </div>
          <div style={styles.qrWrapper}>
            <QRCodeSVG value={qrUri} size={180} />
          </div>
          <div style={styles.enrollmentStep}>
            <div style={styles.stepNum}>2</div>
            <div style={styles.stepText}>
              <strong>Enter the 6-digit code</strong> shown in your authenticator app
              to confirm the setup worked.
            </div>
          </div>
          <div style={styles.totpRow}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={totpCode}
              onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              style={styles.totpInput}
              autoFocus
            />
            <Button onClick={handleConfirm} disabled={loading}>
              {loading ? 'Verifying...' : 'Confirm'}
            </Button>
          </div>
          <label style={styles.trustDeviceLabel}>
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={e => setTrustDevice(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            Trust this device
          </label>
          <Button variant="ghost" onClick={handleCancel} style={{ alignSelf: 'flex-start' }}>
            Cancel
          </Button>

          <div style={styles.appLinksSection}>
            <div style={styles.appLinksTitle}>Don't have an authenticator app?</div>
            <div style={styles.appLinksRow}>
              <a href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noreferrer" style={styles.appLinkBadge}>
                <img src={appStoreBadge} alt="Download on the App Store" height="36" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noreferrer" style={styles.appLinkBadge}>
                <img src={googlePlayBadge} alt="Get it on Google Play" height="36" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Recovery codes */}
      {phase === 'codes' && (
        <div style={styles.enrollmentCard}>
          <div style={styles.recoveryHeader}>Save your recovery codes</div>
          <p style={styles.recoveryDescription}>
            These are one-time-use backup codes. If you ever lose access to your
            authenticator app, you can use one of these to log in instead. Print
            them and store them somewhere secure, like a locked drawer at home.
            Each code can only be used once — once you use one, it is gone. If
            you lose both your authenticator app and your recovery codes, contact
            your administrator to regain access.
          </p>
          <div style={styles.recoveryGrid}>
            {recoveryCodes.map((code, i) => (
              <div key={i} style={styles.recoveryCode}>{code}</div>
            ))}
          </div>
          <label style={styles.savedLabel}>
            <input
              type="checkbox"
              checked={codesSaved}
              onChange={e => setCodesSaved(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            I have saved these recovery codes in a safe place
          </label>
          <Button onClick={handleFinish} disabled={!codesSaved}>
            Done — I've Saved My Codes
          </Button>
        </div>
      )}
    </div>
  );
}

const styles = {
  errorBanner: { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '13px', color: '#991b1b', marginBottom: '16px', fontFamily: 'var(--font-family-base)' },
  actions: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  enrollmentCard: { border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' },
  enrollmentStep: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  stepNum: { width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'var(--color-primary)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-bold)', flexShrink: 0 },
  stepText: { fontSize: '13px', color: 'var(--color-text-secondary)', lineHeight: '1.6', paddingTop: '2px', fontFamily: 'var(--font-family-base)' },
  qrWrapper: { display: 'flex', justifyContent: 'center', padding: '16px', backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' },
  totpRow: { display: 'flex', gap: '12px', alignItems: 'center' },
  totpInput: { width: '140px', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: '20px', letterSpacing: '0.3em', textAlign: 'center', outline: 'none', fontFamily: 'var(--font-family-mono)' },
  recoveryHeader: { fontSize: '15px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  recoveryDescription: { fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: '1.6', margin: 0, fontFamily: 'var(--font-family-base)' },
  recoveryGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' },
  recoveryCode: { fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)', padding: '8px 12px', backgroundColor: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-sm)', textAlign: 'center', letterSpacing: '0.1em', color: 'var(--color-text-primary)' },
  savedLabel: { display: 'flex', alignItems: 'center', fontSize: '13px', color: 'var(--color-text-secondary)', cursor: 'pointer', fontFamily: 'var(--font-family-base)' },
  trustDeviceLabel: { display: 'flex', alignItems: 'center', fontSize: '13px', color: 'var(--color-text-secondary)', cursor: 'pointer', fontFamily: 'var(--font-family-base)' },
  appLinksSection: { borderTop: '1px solid var(--color-border)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  appLinksTitle: { fontSize: '12px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  appLinksRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  appLinkBadge: { display: 'inline-block', lineHeight: 0 },
};